<script setup lang="ts">
import {nextTick, onMounted, ref, watch} from "vue";
import {useDebounceFn} from "@vueuse/core";
import {useSearchPageReload} from "@/service/searchPageReload.service.ts";
import {useLoadingIndicator} from "@/service/loadingIndicator.service.ts";
import type {HtmlPageResult} from "@/service/fetchHtmlPage.ts";
import PaginationNavbar from "@/components/PaginationNavbar.vue";

const props = defineProps<{
  searchPlaceholder?: string,
  loadPage(): Promise<HtmlPageResult>,
}>()

const emit = defineEmits<{
  (e: 'submitSearch'): void
}>()

const search = defineModel<string>('search', {required: true})
const pageSize = defineModel<number>('pageSize', {required: true})
const pageNumber = defineModel<number>('pageNumber', {required: true})

defineSlots<{
  searchForm(props: {}): any
  noneFound(props: {}): any
}>()

const {loadingIndicator, showLoadingIndicator} = useLoadingIndicator()

const totalPages = ref(0)
const totalItems = ref(0)
const loadedHtml = ref('')

const loadPage = useDebounceFn(() => {
  showLoadingIndicator(props.loadPage().then(result => {
    totalPages.value = result.totalPages
    totalItems.value = result.totalItems
    loadedHtml.value = result.htmlContent
    nextTick(() => {
      document.querySelectorAll('.loaded-html img').forEach(img => {
        img.addEventListener('error', () => {
          const imgElement = img as HTMLImageElement
          imgElement.src = 'assets/SMT-Icon.png';
          imgElement.classList.add('g-pa-50');
          imgElement.style.objectFit = 'scale-down';
        }, {once: true})
      })
    })
  }))
}, 50)

function loadFirstPage() {
  pageNumber.value = 0
  loadPage()
}

onMounted(loadPage)
watch(pageNumber, loadPage)
watch(pageSize, loadFirstPage)

useSearchPageReload('reloadFirstPage', loadFirstPage)
</script>

<template>
  <form class="form-group mb-0" @submit.prevent="emit('submitSearch')">
    <div class="input-group u-file-attach-v1 g-brd-gray-light-v2">
      <input
          v-model="search"
          class="form-control form-control-md rounded-0"
          type="text"
          :placeholder="searchPlaceholder"
          :readonly="loadingIndicator"
      >
      <div class="input-group-append">
        <button
            class="btn btn-md u-btn-primary rounded-0"
            type="submit"
            :disabled="loadingIndicator"
            v-text="'Suchen'"
        />
      </div>
    </div>
    <div class="row justify-content-start g-pa-20">
      <slot name="searchForm"/>
    </div>
  </form>

  <div v-if="totalItems > 0" class="text-right">
    <template v-if="totalItems === 1">1 Ergebnis</template>
    <template v-else>{{ totalItems }} Ergebnisse</template>
  </div>

  <div v-html="loadedHtml" class="loaded-html"/>
  <div v-if="totalItems === 0" class="text-center">
    <slot name="noneFound"/>
  </div>

  <div class="container text-center">
    <PaginationNavbar :total-pages="totalPages" v-model:page="pageNumber"/>
  </div>
</template>
